"use client";
import Head from 'next/head';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import QuestionChoice from './components/QuestionChoice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faStar, faStarHalf } from '@fortawesome/free-solid-svg-icons';
import SlideInDiv from '@/app/test/components/SlideInDiv';
import { Question, SiteSettings, LifeMeaningChoice } from '../../utils/types';
import TopMenu from '@/app/test/components/TopMenu';
import { SortableList } from './SortableList';
import { INTRO, INTEREST_QUESTIONS, PERSONALITY_QUESTIONS, SKILL_QUESTIONS, PERSONALITY_INTRO, SKILL_INTRO, LIFE_MEANING_INTRO, LIFE_MEANING_QUESTIONS, OUTRO, prependCoreUrl } from '@/utils/globals';
import Slider from './components/Slider';
import TestPath from './components/TestPath';
import TopRightMenu from './components/TopRightMenu';
import QuestionLoadingSkeleton from './skeleton/QuestionLoadingSkeleton';
import TestModal from './components/TestModal';
import { useRouter } from 'next/navigation';
import { AxiosError } from 'axios';
import usePhoneNumber from '@/hooks/usePhoneNumber';
import { checkTokenSendRequest, sendRequestNoToken } from '@/utils/apiService';


interface Popup {
	higher_than: number;
	lower_than: number;
	questions: string[];
	name: string;
	display_image: string;
	description: string;
  }
  

export default function Home() {
	const router = useRouter();
	const [settings, setSettings] = useState<SiteSettings | null>(null);
	const [isTransitioning, setIsTransitioning] = useState(false);
	const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
	const [loading, setLoading] = useState(true);
	const [isPopUpVisible, setIsPopUpVisible] = useState(false);
	const [currentSection, setCurrentSection] = useState(INTRO);
	const [sliderImages, setSliderImages] = useState([]);
	const { phoneNumber, isCookieLoading } = usePhoneNumber();
	const [popUps, setPopUps] = useState<Popup[]>([]);


	const [interestQuestions, setInterestQuestions] = useState<Question[]>([]);
	const [personalityQuestions, setPersonalityQuestions] = useState<Question[]>([]);
	const [skillQuestions, setSkillQuestions] = useState<Question[]>([]);
	const [lifeMeaningChoices, setLifeMeaningChoices] = useState<LifeMeaningChoice[]>([]);

	const [currentInterestIndex, setCurrentInterestIndex] = useState(0);
	const [currentPersonalityIndex, setCurrentPersonalityIndex] = useState(0);
	const [currentSkillIndex, setCurrentSkillIndex] = useState(0);
	const [continueSectionIndex, setContinueSectionIndex] = useState(0);

	const currentInterestQuestion = interestQuestions[currentInterestIndex];
	const currentPersonalityQuestion = personalityQuestions[currentPersonalityIndex];
	const currentSkillQuestion = skillQuestions[currentSkillIndex];


	const [popUpDescription, setPopUpDescription] = useState("");
	const [popUpImage, setPopUpImage] = useState("");
	const [popUpName, setPopUpName] = useState("");

	const goToErrorPage = useCallback(() => {
		router.push('/error');
	}, [router]);


	const interestChoices = [
		{ number: 5, text: 'Маш сонирхолтой' },
		{ number: 4, text: 'Сонирхолтой' },
		{ number: 3, text: 'Сайн мэдэхгүй' },
		{ number: 2, text: 'Сонирхолгүй' },
		{ number: 1, text: 'Огт сонирхолгүй' },
	];


	const personalityChoices = [
		{ number: 5, text: 'Бүрэн санал нийлнэ' },
		{ number: 4, text: 'Санал нийлнэ' },
		{ number: 3, text: 'Сайн мэдэхгүй' },
		{ number: 2, text: 'Санал нийлэхгүй' },
		{ number: 1, text: 'Огт санал нийлэхгүй' },
	];


	const skillChoices = [
		{ number: 5, text: 'Маш өндөр хөгжсөн' },
		{ number: 4, text: 'Өндөр хөгжсөн' },
		{ number: 3, text: 'Дундаж түвшинд хөгжсөн' },
		{ number: 2, text: 'Бага хөгжсөн' },
		{ number: 1, text: 'Маш бага хөгжсөн' },
	];

	const didFetchData = useRef(false);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const data = await checkTokenSendRequest('GET', '/api/test-settings/');
                setPopUps(data.pop_ups);
				setSettings(data.settings);
				setInterestQuestions(data.interest_questions);
				setPersonalityQuestions(data.personality_questions);
				setSkillQuestions(data.skill_questions);
				setSliderImages(data.images);
				setLifeMeaningChoices(
					data.life_meaning_choices.map((choice: any, index: number) => ({
						...choice,
						currentOrder: index,
					}))
				);
				await checkTestUuid();
			} catch (error) {
				if (error instanceof AxiosError && error.code === AxiosError.ERR_BAD_REQUEST) {
					goToErrorPage();
				}
			} finally {
				setLoading(false);
			}
		};

		const checkTestUuid = async () => {
			const test_uuid = localStorage.getItem('test_uuid');
			const data = await checkTokenSendRequest('POST', '/api/test/retrieve-answer/', { uuid: test_uuid });
			localStorage.setItem('test_uuid', data.uuid);
			if (data.life_meaning_fill) {
				setContinueSectionIndex(OUTRO);
			} else if (data.skill_fill) {
				setContinueSectionIndex(LIFE_MEANING_QUESTIONS);
			} else if (data.personality_fill) {
				setContinueSectionIndex(SKILL_QUESTIONS);
			} else if (data.interest_fill) {
				setContinueSectionIndex(PERSONALITY_QUESTIONS);
			} else {
				setContinueSectionIndex(0);
			}
		};

		if (!didFetchData.current) {
			fetchData();
			didFetchData.current = true;
		}
	}, [goToErrorPage]);


	const updateItems = (items: any[], index: number, value: number, key: string) => {
		return items.map((item, idx) =>
			idx === index ? { ...item, [key]: value } : item
		);
	};

	const transitionInProgress = useRef(false);

	const processTransition = useCallback((nextSection: number) => {
        if (transitionInProgress.current) return;
        transitionInProgress.current = true;
        if (
            (currentSection === INTEREST_QUESTIONS && interestQuestions.length - 1 === currentInterestIndex) ||
            (currentSection === PERSONALITY_QUESTIONS && personalityQuestions.length - 1 === currentPersonalityIndex) ||
            (currentSection === SKILL_QUESTIONS && skillQuestions.length - 1 === currentSkillIndex)
        ) {
            setCurrentSection(nextSection);
            transitionInProgress.current = false; // Transition complete
        } else {
            setDirection('forward');
            setIsTransitioning(true);
            setTimeout(() => {
                if (currentSection === INTEREST_QUESTIONS) {
                    setCurrentInterestIndex(prevIndex => prevIndex + 1);
                } else if (currentSection === PERSONALITY_QUESTIONS) {
                    setCurrentPersonalityIndex(prevIndex => prevIndex + 1);
                } else if (currentSection === SKILL_QUESTIONS) {
                    setCurrentSkillIndex(prevIndex => prevIndex + 1);
                }
                setIsTransitioning(false);
                transitionInProgress.current = false; // Transition complete
            }, 300);
        }
    },[currentSection, currentInterestIndex, currentPersonalityIndex, currentSkillIndex, interestQuestions, personalityQuestions, skillQuestions]);


	useEffect(() => {
		if (personalityQuestions.length === 0) return;
	
	
		const filteredPopUps = popUps.filter(popup => 
			popup.questions.includes(personalityQuestions[currentPersonalityIndex].code)
		);

		const filteredQuestionCodes = filteredPopUps.flatMap(popup => popup.questions);

		const hasUndefinedChoice = filteredQuestionCodes.some(code => {
			const question = personalityQuestions.find(q => q.code === code);
			return question?.selectedChoice === undefined;
		});

		if (hasUndefinedChoice) {
			// console.log('One or more selectedChoices for filtered questions are undefined. Stopping processing.');
			return;
		}
	
		const popUpSums = filteredPopUps.map(popup => {
			const choicesSum = popup.questions.reduce((popupTotal, questionCode) => {
				const question = personalityQuestions.find(q => q.code === questionCode);
				const choiceValue = question?.selectedChoice ?? 0; // Handle undefined
				return popupTotal + (choiceValue-1);
			}, 0);
			const maxSum = 4 * popup.questions.length;
			return {
				higher_than:popup.higher_than,
				lower_than:popup.lower_than,
				display_image:popup.display_image,
				description: popup.description,
				name: popup.name,
				value: choicesSum / maxSum * 100
			};
		});
	
		popUpSums.forEach(({ name, value, higher_than, lower_than, display_image, description }) => {
			console.log(`Sum of selectedChoices for "${name}":`, value)
			if(value >= higher_than && value <= lower_than){
			// console.log(`Sum of selectedChoices for "${name}":`, value)
				setPopUpName(name)
				setPopUpImage(display_image)
				setPopUpDescription(description)
				setIsPopUpVisible(true);
			}
		});
	}, [personalityQuestions, currentPersonalityIndex, popUps]);




	const handleChoiceClick = useCallback((number: number) => {
        if (transitionInProgress.current) return;
        if (currentSection === INTEREST_QUESTIONS) {
            setInterestQuestions(updateItems(interestQuestions, currentInterestIndex, number, 'selectedChoice'));
            setTimeout(() => { processTransition(PERSONALITY_INTRO); }, 100);
        } else if (currentSection === PERSONALITY_QUESTIONS) {
            setPersonalityQuestions(updateItems(personalityQuestions, currentPersonalityIndex, number, 'selectedChoice'));
            setTimeout(() => { processTransition(SKILL_INTRO); }, 100);
        } else if (currentSection === SKILL_QUESTIONS) {
            setSkillQuestions(updateItems(skillQuestions, currentSkillIndex, number, 'selectedChoice'));
            setTimeout(() => { processTransition(LIFE_MEANING_INTRO); }, 100);
        }
    }, [currentSection, currentInterestIndex, currentPersonalityIndex, currentSkillIndex, interestQuestions, personalityQuestions, skillQuestions, processTransition]);

	const handleChevronClick = () => {
		setDirection('backward');
		setIsTransitioning(true);
		setTimeout(() => {
			if (currentSection === INTEREST_QUESTIONS) {
				setCurrentInterestIndex(prevIndex => Math.max(prevIndex - 1, 0));
			}
			if (currentSection === PERSONALITY_QUESTIONS) {
				setCurrentPersonalityIndex(prevIndex => Math.max(prevIndex - 1, 0));
			}
			if (currentSection === SKILL_QUESTIONS) {
				setCurrentSkillIndex(prevIndex => Math.max(prevIndex - 1, 0));
			}
			setIsTransitioning(false);
		}, 300);
	};


	const handleSlideInButtonClick = () => {
		setIsPopUpVisible(!isPopUpVisible);
	};
	const handlePopUpClose = () => {
		setIsPopUpVisible(false);
	};

	const [isModalOpen, setIsModalOpen] = useState(false);

	const handleOpenModal = () => { setIsModalOpen(true); setIsPopUpVisible(false) };
	const handleCloseModal = () => setIsModalOpen(false);

	const handleDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setCurrentSection(Number(event.target.value));
	};

	const moveItem = (fromIndex: number, toIndex: number) => {
		if (toIndex < 0 || toIndex >= lifeMeaningChoices.length) return;
		const updatedChoices = [...lifeMeaningChoices];
		const [movedItem] = updatedChoices.splice(fromIndex, 1);
		updatedChoices.splice(toIndex, 0, movedItem);
		setLifeMeaningChoices(updatedChoices);
	};


	const sendData = async (fromIndex: number) => {
		setLoading(true);
		const test_uuid = localStorage.getItem("test_uuid");
		if (test_uuid === null) {
			goToErrorPage();
			setLoading(false);
			return;
		}
		try {
			let answerType: string;
			let dataToSend: any;

			switch (fromIndex) {
				case PERSONALITY_INTRO:
					answerType = 'interest';
					dataToSend = JSON.stringify(interestQuestions.map((question) => ({
						code: question.code,
						selectedChoice: question.selectedChoice || 1
					})));
					break;

				case SKILL_INTRO:
					answerType = 'personality';
					dataToSend = JSON.stringify(personalityQuestions.map((question) => ({
						code: question.code,
						selectedChoice: question.selectedChoice || 1
					})));
					break;

				case LIFE_MEANING_INTRO:
					answerType = 'skill';
					dataToSend = JSON.stringify(skillQuestions.map((question) => ({
						code: question.code,
						selectedChoice: question.selectedChoice || 1
					})));
					break;

				case LIFE_MEANING_QUESTIONS:
					answerType = 'life_meaning';
					dataToSend = JSON.stringify(lifeMeaningChoices.map((choice,index) => ({
						code: choice.code,
						currentOrder: index
					})));
					break;
				default:
					console.error('Invalid fromIndex value:', fromIndex);
					return;
			}
			await checkTokenSendRequest('POST', '/api/test/post-answer/', { "uuid": test_uuid, "answer_type": answerType, "fill": dataToSend });
			setCurrentSection(fromIndex + 1);
		} catch (error) {
			if (error instanceof AxiosError && error.code === AxiosError.ERR_BAD_REQUEST) {
				localStorage.removeItem("test_uuid")
				goToErrorPage()
			}
		} finally {
			setLoading(false);
		}
	};


	const startTest = async () => {
		setCurrentSection(INTRO + 1);
	};


	const finishTest = () => {
		router.push(`/result/${localStorage.getItem("test_uuid")}`)
	};


	useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (["1", "2", "3", "4", "5"].includes(event.key)) {
                handleChoiceClick(parseInt(event.key, 10));
            }
        };

        document.addEventListener("keydown", handleKeyPress);

        return () => {
            document.removeEventListener("keydown", handleKeyPress);
        };
    }, [currentSection, currentInterestIndex, currentPersonalityIndex, currentSkillIndex, transitionInProgress, handleChoiceClick]);


	return (
		<>
			<TestModal isOpen={isModalOpen} onClose={handleCloseModal} title="Тайлбар">
			<div className='' dangerouslySetInnerHTML={{ __html: prependCoreUrl(popUpDescription) || "" }} />
			</TestModal>
			<TopMenu />
			<TopRightMenu isLoading={isCookieLoading} phoneNumber={phoneNumber} />

			<main className={`flex`}>
				{/* <div className='fixed top-[130px] flex flex-col z-10 opacity-50'>
					<button
						className='bg-gray-500 text-white py-3 px-4 rounded-md text-lg font-semibolds'
						onClick={handleSlideInButtonClick}
					>
						Pop up
					</button>
					<select
						id="sectionDropdown"
						className="bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded-md focus:outline-none focus:border-gray-500"
						value={currentSection}
						onChange={handleDropdownChange}
					>
						<option value={INTRO}>Introduction</option>
						<option value={INTEREST_QUESTIONS}>Interest Questions</option>
						<option value={PERSONALITY_INTRO}>Personality Intro</option>
						<option value={PERSONALITY_QUESTIONS}>Personality Questions</option>
						<option value={SKILL_INTRO}>Skill Intro</option>
						<option value={SKILL_QUESTIONS}>Skill Questions</option>
						<option value={LIFE_MEANING_INTRO}>Life Meaning Intro</option>
						<option value={LIFE_MEANING_QUESTIONS}>Life Meaning Questions</option>
						<option value={OUTRO}>Outro</option>
					</select>
				</div> */}

				<div className='relative cursor-pointer'
					onClick={handleOpenModal}>
					<SlideInDiv isVisible={isPopUpVisible} onClose={handlePopUpClose} text={popUpName} imageUrl={popUpImage}/>
				</div>

				{!loading && currentInterestIndex != 0 && currentSection == INTEREST_QUESTIONS ? (
					<div className='text-xl mt-[72px] md:w-3/4 w-full dark:bg-dark-900 bg-gray-200 fixed ps-5 py-3 z-10'>
						{settings?.interest_test_question}
					</div>) : (<></>)
				}


				{!loading && currentPersonalityIndex != 0 && currentSection == PERSONALITY_QUESTIONS ? (
					<div className='text-xl mt-[72px] md:w-3/4 w-full dark:bg-dark-900 bg-gray-200 fixed ps-5 py-3 z-10'>
						{settings?.personality_test_question}
					</div>) : (<></>)
				}


				{!loading && currentSkillIndex != 0 && currentSection == SKILL_QUESTIONS ? (
					<div className='text-xl mt-[72px] md:w-3/4 w-full dark:bg-dark-900 bg-gray-200 fixed ps-5 py-3 z-10'>
						{settings?.skill_test_question}
					</div>) : (<></>)
				}


				{!loading && currentSection == LIFE_MEANING_QUESTIONS ? (
					<div className='text-xl mt-[72px] md:w-3/4 w-full dark:bg-dark-900 bg-gray-200 fixed ps-5 py-3 z-10'>
						{settings?.life_meaning_test_question}
					</div>) : (<></>)
				}



				<div className="pt-[150px] pb-[150px] w-full md:w-3/4 p-4 min-h-screen dark:bg-dark-700 flex flex-col justify-center items-center overflow-hidden">
					<div className={`
						${currentSection == INTRO && ('md:w-[500px]')}
						${currentSection == INTEREST_QUESTIONS && ('md:w-[500px]')}
						${currentSection == PERSONALITY_INTRO && ('md:w-[500px]')}
						${currentSection == PERSONALITY_QUESTIONS && ('md:w-[500px]')}
						${currentSection == SKILL_INTRO && ('md:w-[500px]')}
						${currentSection == SKILL_QUESTIONS && ('md:w-[500px]')}
						${currentSection == LIFE_MEANING_INTRO && ('md:w-[500px]')}
						${currentSection == LIFE_MEANING_QUESTIONS && ('md:w-[500px]')}
						${currentSection == OUTRO && ('md:w-[500px]')}
						px-3 w-full 
						${isTransitioning ? (direction === 'forward' ? 'question-exit' : 'question-exit-reverse') : (direction === 'forward' ? 'question-enter' : 'question-enter-reverse')}
						`}>

						{loading ? (
							<QuestionLoadingSkeleton />
						) : (
							<>
								{currentSection === INTRO && (
									<div className='flex flex-col'>
										<div className='text-3xl font-semibold text-center'>myCareer-д тавтай морил</div>
										<div className='flex flex-row mt-5'>
											<div className='w-1/2 space-y-1 text-center'>
												<div>30 минут</div>
												<div className='uppercase'>Карьер тест</div>
											</div>
											<div className='w-1/2 space-y-1 text-center'>
												<div>Хязгааргүй</div>
												<div className='uppercase'>Боломжууд</div>
											</div>
										</div>
										<div className='mt-5'>
											{continueSectionIndex === 0 && <button
												className='w-full bg-purple-500 text-white py-3 px-4 rounded-md text-lg font-semibold 
             transition-transform transform hover:trandark-y-0.5 hover:shadow-lg'
												onClick={() => { startTest() }}
											>
												Тестийг бөглөж эхлэх
											</button>}


											{continueSectionIndex !== 0 && <button
												className='w-full bg-purple-500 text-white py-3 px-4 rounded-md text-lg font-semibold 
             transition-transform transform hover:trandark-y-0.5 hover:shadow-lg'
												onClick={() => { setCurrentSection(continueSectionIndex) }}
											>
												Үргэлжлүүлэн бөглөх
											</button>}

										</div>
										<div className='mt-5'>
											<Slider images={sliderImages} />
										</div>

									</div>
								)}
								{currentSection === INTEREST_QUESTIONS &&
									<div className=''>
										<div className="flex mt-4 text-lg font-semibold dark:text-gray-300 text-gray-600">
											{currentInterestIndex === 0 ? (
												<div className="text-lg font-semibold dark:text-gray-300 text-gray-600">{settings?.interest_test_question}</div>
											) : (
												<button onClick={handleChevronClick} className="mr-2">
													<FontAwesomeIcon icon={faChevronUp} />
												</button>
											)}
										</div>
										<div className="text-2xl">{currentInterestQuestion.text}</div>
										<div className="mt-5 font-bold space-y-2">
											{interestChoices.map((choice) => (
												<QuestionChoice
													key={choice.number}
													number={choice.number}
													text={choice.text}
													onClick={handleChoiceClick}
													isSelected={currentInterestQuestion.selectedChoice === choice.number}
												/>
											))}
										</div>
									</div>}
								{currentSection === PERSONALITY_INTRO &&
									<div className='text-center'>
										<div className='font-thin text-md'>Дараагийн алхам</div>
										<div className='font-thin text-3xl'>{settings?.personality_test_name}</div>
										<div className='font-thin text-md mt-3 '>{settings?.personality_test_description}</div>
										<div className='mt-5'>
											<button
												className='w-full bg-purple-500 text-white py-3 px-4 rounded-md text-lg font-semibold 
             transition-transform transform hover:trandark-y-0.5 hover:shadow-lg'
												onClick={() => sendData(PERSONALITY_INTRO)}
											>
												Хадгалаад үргэлжлүүлэх
											</button>

										</div>
									</div>
								}
								{currentSection === PERSONALITY_QUESTIONS &&
									<>
										<div className="flex mt-4 text-lg font-semibold dark:text-gray-300 text-gray-600">
											{currentPersonalityIndex === 0 ? (
												<div className="text-lg font-semibold dark:text-gray-300 text-gray-600">{settings?.personality_test_question}</div>
											) : (
												<button onClick={handleChevronClick} className="mr-2">
													<FontAwesomeIcon icon={faChevronUp} />
												</button>
											)}
										</div>
										<div className="text-2xl">{currentPersonalityQuestion.text}</div>
										<div className="mt-5 font-bold space-y-2">
											{personalityChoices.map((choice) => (
												<QuestionChoice
													key={choice.number}
													number={choice.number}
													text={choice.text}
													onClick={handleChoiceClick}
													isSelected={currentPersonalityQuestion.selectedChoice === choice.number}
												/>
											))}
										</div>
									</>
								}
								{currentSection === SKILL_INTRO &&
									<div className='text-center'>
										<div className='font-thin text-md'>Дараагийн алхам</div>
										<div className='font-thin text-3xl'>{settings?.skill_test_name}</div>
										<div className='font-thin text-md mt-3 '>{settings?.skill_test_description}</div>
										<div className='mt-5'>
											<button
												className='w-full bg-purple-500 text-white py-3 px-4 rounded-md text-lg font-semibold 
             transition-transform transform hover:trandark-y-0.5 hover:shadow-lg'
												onClick={() => { sendData(SKILL_INTRO) }}
											>
												Хадгалаад үргэлжлүүлэх
											</button>
										</div>
									</div>
								}
								{currentSection === SKILL_QUESTIONS &&
									<div className=''>
										<div className="flex mt-4 text-lg font-semibold dark:text-gray-300 text-gray-600">
											{currentSkillIndex === 0 ? (
												<div className="text-lg font-semibold dark:text-gray-300 text-gray-600">{settings?.skill_test_question}</div>
											) : (
												<button onClick={handleChevronClick} className="mr-2">
													<FontAwesomeIcon icon={faChevronUp} />
												</button>
											)}
										</div>
										<div className="text-2xl mt-2">{currentSkillQuestion.name}</div>
										<div className="mt-1">{currentSkillQuestion.text}</div>
										<div className="mt-5 font-bold space-y-2">
											{skillChoices.map((choice) => (
												<QuestionChoice
													key={choice.number}
													number={choice.number}
													text={choice.text}
													onClick={handleChoiceClick}
													isSelected={currentSkillQuestion.selectedChoice === choice.number}
												/>
											))}
										</div>
									</div>
								}

								{currentSection === LIFE_MEANING_INTRO &&
									<div className='text-center'>
										<div className='font-thin text-md'>Дараагийн алхам</div>
										<div className='font-thin text-3xl'>{settings?.life_meaning_test_name}</div>
										<div className='font-thin text-md mt-3'>{settings?.life_meaning_test_description}</div>
										<div className='mt-5'>
											<button
												className='w-full bg-purple-500 text-white py-3 px-4 rounded-md text-lg font-semibold
													transition-transform transform hover:trandark-y-0.5 hover:shadow-lg'
												onClick={() => { sendData(LIFE_MEANING_INTRO) }}
											>
												Хадгалаад үргэлжлүүлэх
											</button>
										</div>
									</div>
								}

								{currentSection == LIFE_MEANING_QUESTIONS &&
									<div className='z-0'>
										<SortableList
											items={lifeMeaningChoices}
											onChange={setLifeMeaningChoices}
											renderItem={(item: LifeMeaningChoice, index: number) => (
												<SortableList.Item
													index={item.code}
													id={item.id}
													onMoveUp={() => moveItem(index, index - 1)}
													onMoveDown={() => moveItem(index, index + 1)}
													disableUp={index === 0}
													disableDown={index === lifeMeaningChoices.length - 1}
												>
													<div className='m-2 flex select-none'>
														<div>
															<div className='text-lg font-bold'>{index+1}. {item.name}</div>
															<div className='text-sm'>{item.subtitle}</div>
														</div>
													</div>
													<SortableList.DragHandle />
												</SortableList.Item>
											)}
										/>
										<button
											className='w-full bg-purple-500 text-white py-3 px-4 mt-5 rounded-md text-lg font-semibold 
											transition-transform transform hover:trandark-y-0.5 hover:shadow-lg'
											onClick={() => { sendData(LIFE_MEANING_QUESTIONS) }}
										>
											Хадгалаад үргэлжлүүлэх
										</button>
									</div>
								}


								{currentSection == OUTRO && (
									<div className='flex flex-col'>
										<div className='text-3xl font-semibold text-center'>{settings?.outro_title}</div>
										<div className='flex flex-row mt-5 text center'>
											{settings?.outro_body}
										</div>
										<div className='mt-5'>
											<button
												className='w-full bg-purple-500 text-white py-3 px-4 rounded-md text-lg font-semibold 
		 transition-transform transform hover:trandark-y-0.5 hover:shadow-lg'
												onClick={() => { finishTest() }}
											>
												Үр дүн харах
											</button>

										</div>

									</div>
								)}
							</>
						)}
					</div>
				</div>

				<TestPath
					settings={settings}
					currentSection={currentSection}
					INTEREST_QUESTIONS={INTEREST_QUESTIONS}
					PERSONALITY_QUESTIONS={PERSONALITY_QUESTIONS}
					SKILL_QUESTIONS={SKILL_QUESTIONS}
					LIFE_MEANING_QUESTIONS={LIFE_MEANING_QUESTIONS}
					interestQuestions={interestQuestions}
					currentInterestIndex={currentInterestIndex}
					personalityQuestions={personalityQuestions}
					currentPersonalityIndex={currentPersonalityIndex}
					skillQuestions={skillQuestions}
					currentSkillIndex={currentSkillIndex}
				/>
			</main>
		</>
	);
}